import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  createShopifyCart,
  addLineToShopifyCart,
  updateShopifyCartLine,
  removeLineFromShopifyCart,
  fetchCartStatus,
  formatCheckoutUrl,
  type CartItem,
} from "@/lib/shopify-cart";
import { trackAddToCart } from "@/lib/analytics";

interface CartStore {
  items: CartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  isLoading: boolean;
  isSyncing: boolean;
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  addItem: (item: Omit<CartItem, "lineId">) => Promise<void>;
  updateQuantity: (variantId: string, quantity: number) => Promise<void>;
  removeItem: (variantId: string) => Promise<void>;
  clearCart: () => void;
  syncCart: () => Promise<void>;
  getCheckoutUrl: () => string | null;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      isLoading: false,
      isSyncing: false,
      isOpen: false,
      setOpen: (open) => set({ isOpen: open }),

      addItem: async (item) => {
        const { items, cartId, clearCart } = get();
        const existing = items.find((i) => i.variantId === item.variantId);
        // Cap requested quantity at the variant's known stock so the customer
        // can never end up with more units in the cart than Shopify will let
        // them buy. Unknown stock = no cap.
        const stock = item.quantityAvailable;
        const knownStock = typeof stock === "number";
        const currentQty = existing?.quantity ?? 0;
        const maxAddable = knownStock ? Math.max((stock as number) - currentQty, 0) : item.quantity;
        const addQty = Math.min(item.quantity, maxAddable);
        if (addQty <= 0) {
          // Already at max stock — open the drawer so the user sees why.
          set({ isOpen: true });
          return;
        }
        const itemToAdd = { ...item, quantity: addQty };
        set({ isLoading: true });
        // Fire analytics for every add — covers new cart, existing line, and
        // brand-new variant equally. Quantity is the delta the user requested,
        // not the resulting line total.
        trackAddToCart({
          quantity: itemToAdd.quantity,
          product: {
            id: itemToAdd.product.node.id,
            handle: itemToAdd.product.node.handle,
            title: itemToAdd.product.node.title,
            price: itemToAdd.price,
            variantId: itemToAdd.variantId,
          },
        });
        try {
          if (!cartId) {
            const result = await createShopifyCart({ ...itemToAdd, lineId: null });
            if (result) {
              set({
                cartId: result.cartId,
                checkoutUrl: result.checkoutUrl,
                items: [{ ...itemToAdd, lineId: result.lineId }],
                isOpen: true,
              });
            }
          } else if (existing) {
            const newQty = existing.quantity + itemToAdd.quantity;
            if (!existing.lineId) return;
            const result = await updateShopifyCartLine(cartId, existing.lineId, newQty);
            if (result.success) {
              const cur = get().items;
              set({
                items: cur.map((i) =>
                  i.variantId === itemToAdd.variantId
                    ? { ...i, quantity: newQty, quantityAvailable: itemToAdd.quantityAvailable ?? i.quantityAvailable }
                    : i
                ),
                isOpen: true,
              });
            } else if (result.cartNotFound) {
              clearCart();
            }
          } else {
            const result = await addLineToShopifyCart(cartId, { ...itemToAdd, lineId: null });
            if (result.success) {
              const cur = get().items;
              set({
                items: [...cur, { ...itemToAdd, lineId: result.lineId ?? null }],
                isOpen: true,
              });
            } else if (result.cartNotFound) {
              clearCart();
            }
          }
        } catch (e) {
          console.error("addItem failed:", e);
        } finally {
          set({ isLoading: false });
        }
      },

      updateQuantity: async (variantId, quantity) => {
        if (quantity <= 0) {
          await get().removeItem(variantId);
          return;
        }
        const { items, cartId, clearCart } = get();
        const item = items.find((i) => i.variantId === variantId);
        if (!item?.lineId || !cartId) return;
        // Cap at known stock so the +/- buttons can never overshoot.
        const stock = item.quantityAvailable;
        const cappedQty =
          typeof stock === "number" ? Math.max(1, Math.min(quantity, stock)) : quantity;
        set({ isLoading: true });
        try {
          const result = await updateShopifyCartLine(cartId, item.lineId, cappedQty);
          if (result.success) {
            const cur = get().items;
            set({
              items: cur.map((i) =>
                i.variantId === variantId ? { ...i, quantity: cappedQty } : i
              ),
            });
          } else if (result.cartNotFound) {
            clearCart();
          }
        } finally {
          set({ isLoading: false });
        }
      },

      removeItem: async (variantId) => {
        const { items, cartId, clearCart } = get();
        const item = items.find((i) => i.variantId === variantId);
        if (!item?.lineId || !cartId) return;
        set({ isLoading: true });
        try {
          const result = await removeLineFromShopifyCart(cartId, item.lineId);
          if (result.success) {
            const cur = get().items;
            const next = cur.filter((i) => i.variantId !== variantId);
            if (next.length === 0) clearCart();
            else set({ items: next });
          } else if (result.cartNotFound) {
            clearCart();
          }
        } finally {
          set({ isLoading: false });
        }
      },

      clearCart: () => set({ items: [], cartId: null, checkoutUrl: null }),
      getCheckoutUrl: () => get().checkoutUrl,

      syncCart: async () => {
        const { cartId, isSyncing, clearCart } = get();
        if (!cartId || isSyncing) return;
        set({ isSyncing: true });
        try {
          const data = await fetchCartStatus(cartId);
          if (!data) return;
          const cart = data?.data?.cart;
          if (!cart || cart.totalQuantity === 0) clearCart();
        } catch (e) {
          console.error("syncCart failed:", e);
        } finally {
          set({ isSyncing: false });
        }
      },
    }),
    {
      name: "farveladeland-cart",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        items: state.items,
        cartId: state.cartId,
        checkoutUrl: state.checkoutUrl,
      }),
      // Rewrite any persisted checkoutUrl that points to a now-removed
      // custom domain (e.g. farveladeland.com) so existing visitors with
      // a stale cart in localStorage still get a working checkout URL.
      onRehydrateStorage: () => (state) => {
        if (state?.checkoutUrl) {
          state.checkoutUrl = formatCheckoutUrl(state.checkoutUrl);
        }
      },
    }
  )
);
